import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { CustomTooltip } from './tooltip';

// Array com 100 cores fixas (hex)
const fixedColors = [
    '#4A90E2', // Blue
    '#1B3A7C', // Dark Blue
    '#F5A623', // Orange
    '#D622A9', // Magenta
    '#BD10E0', // Purple
    '#9013FE', // Violet
    '#50E3C2', // Aqua
    '#7ED321', // Green
    '#B8E986', // Light Green
    '#F8E71C', // Yellow
    '#F45B69', // Coral
    '#417505', // Olive Green
    '#FF7F50', // Coral Pink
    '#7F8C8D', // Neutral Gray
    '#3E4A59', // Slate Gray
    '#FF5733',
    '#C70039',
    '#900C3F',
    '#581845',
    '#FFC300',
    '#DAF7A6',
    '#1ABC9C',
    '#2ECC71',
    '#3498DB',
    '#9B59B6',
    '#34495E',
    '#16A085',
    '#27AE60',
    '#2980B9',
    '#8E44AD',
    '#2C3E50',
    '#F1C40F',
    '#E67E22',
    '#E74C3C',
    '#ECF0F1',
    '#95A5A6',
    '#F39C12',
    '#D35400',
    '#C0392B',
    '#BDC3C7',
    '#FF9999',
    '#FF6666',
    '#FF3333',
    '#FF0000',
    '#CC0000',
    '#990000',
    '#660000',
    '#330000',
    '#FFCC99',
    '#FF9966',
    '#FF6633',
    '#CC3300',
    '#993300',
    '#663300',
    '#FFCC00',
    '#FF9900',
    '#FF6600',
    '#CC6600',
    '#FFFF99',
    '#FFFF66',
    '#FFFF33',
    '#FFFF00',
    '#CCCC00',
    '#999900',
    '#666600',
    '#333300',
    '#99FF99',
    '#66FF66',
    '#33FF33',
    '#00FF00',
    '#00CC00',
    '#009900',
    '#006600',
    '#003300',
    '#99FFFF',
    '#66FFFF',
    '#33FFFF',
    '#00FFFF',
    '#00CCCC',
    '#009999',
    '#006666',
    '#003333',
    '#9999FF',
    '#6666FF',
    '#3333FF',
    '#0000FF',
    '#0000CC',
    '#000099',
    '#000066',
    '#000033',
    '#E6B0AA',
    '#D98880',
    '#C39BD3',
    '#7FB3D5',
    '#76D7C4',
    '#7DCEA0',
    '#F7DC6F',
    '#F0B27A',
    '#E59866',
    '#B22222'
];

const PieChartWithDetails = memo(({ data = [], categories = [], onClick, activeFilters = [], disabled = false }) => {
    const { t } = useTranslation();

    // Calcula o total e prepara os dados originais
    const total = data.reduce((acc, val) => acc + val, 0);
    const originalData = data.map((value, index) => ({
        name: categories[index],
        value,
        percent: total > 0 ? (value / total) * 100 : 0,
    }));

    // Ordena os dados sempre do maior para o menor
    const sortedListData = [...originalData].sort((a, b) => b.value - a.value);

    // 1) constantes de threshold e max
    const PERCENT_THRESHOLD = 2; // <2% considera “pequeno”
    const MAX_SLICES = 10; // máximo de fatias incluindo OTHERS

    // 2) separa sortedListData em grandes e pequenas
    const smallSlices = sortedListData.filter(i => i.percent < PERCENT_THRESHOLD);
    const bigSlices = sortedListData.filter(i => i.percent >= PERCENT_THRESHOLD);

    // 3) pega só as top (MAX_SLICES - 1) fatias grandes
    const visibleBig = bigSlices.slice(0, MAX_SLICES - 1);
    const overflowBig = bigSlices.slice(MAX_SLICES - 1); // restante vira Others

    // 4) monta o grupo “OTHERS” com todas as pequenas + overflow
    const othersGroup = [...smallSlices, ...overflowBig];
    const othersValue = othersGroup.reduce((sum, i) => sum + i.value, 0);
    const othersPercent = othersGroup.reduce((sum, i) => sum + i.percent, 0);

    // 5) monta chartData: big visíveis + OTHERS (se houver)
    const chartData = [
        ...visibleBig,
        ...(othersGroup.length
            ? [{ name: "OTHERS", value: othersValue, percent: othersPercent }]
            : [])
    ];

    // Mapeamento de cores utilizando as 100 cores fixas
    const categoryColorMap = {};
    chartData.forEach((entry, index) => {
        categoryColorMap[entry.name] = fixedColors[index % fixedColors.length];
    });
    if (chartData.some(item => item.name === "OTHERS")) {
        categoryColorMap["OTHERS"] = "#cccccc";
    }

    // Estados para rastrear categorias ativas, a categoria em hover e controle da animação
    const [activeCategories, setActiveCategories] = useState([]);
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const [hasAnimated, setHasAnimated] = useState(false);

    useEffect(() => {
        if (JSON.stringify(activeFilters) !== JSON.stringify(activeCategories)) {
            setActiveCategories(activeFilters);
        }
    }, [activeFilters]);

    const toggleSelection = (category) => {
        if (disabled) return;
        if (category === "OTHERS") return;
        setActiveCategories((prev) => {
            const isActive = prev.includes(category);
            const newCategories = isActive ? prev.filter(c => c !== category) : [...prev, category];
            onClick && onClick(category);
            return newCategories;
        });
    };

    // Correção: uso de "chartData" em vez de "chartDataGrouped"
    const onPieClick = (_, index) => {
        if (index >= 0 && index < chartData.length) {
            const clickedItem = chartData[index];
            if (clickedItem && clickedItem.name !== "OTHERS") {
                toggleSelection(clickedItem.name);
            }
        }
    };

    const onListItemMouseEnter = (category) => {
        setHoveredCategory(category);
    };

    const onListItemMouseLeave = () => {
        setHoveredCategory(null);
    };

    const onListItemClick = (category, e) => {
        e.stopPropagation();
        toggleSelection(category);
    };

    if (!data.length || !categories.length || data.length !== categories.length) {
        return <p>{t('Dashboard.Not')}</p>;
    }

    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <ResponsiveContainer width="60%" height={250}>
                <PieChart>
                    <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={75}
                        label={({ name, value, percent, cx, cy, midAngle, outerRadius }) => {
                            const RADIAN = Math.PI / 180;
                            const radius = outerRadius + 25;
                            const x = cx + radius * Math.cos(-midAngle * RADIAN);
                            const y = cy + radius * Math.sin(-midAngle * RADIAN);
                            return (
                                <text
                                    x={x}
                                    y={y}
                                    fill="black"
                                    textAnchor={x > cx ? "start" : "end"}
                                    dominantBaseline="central"
                                    fontSize="12px"
                                    style={{
                                        background: "rgba(255, 255, 255, 0.8)",
                                        padding: "2px",
                                        borderRadius: "3px"
                                    }}
                                >
                                    <tspan fontWeight="bold">{`${value} (${percent.toFixed(2)}%)`}</tspan>
                                </text>
                            );
                        }}
                        isAnimationActive={!hasAnimated}
                        animationBegin={0}
                        animationDuration={500}
                        animationEasing="ease-out"
                        onAnimationEnd={() => setHasAnimated(true)}
                        onClick={onPieClick}
                        cursor={disabled ? "not-allowed" : "pointer"}
                    >
                        {chartData.map((entry, index) => {
                            const opacity = hoveredCategory
                                ? (entry.name === hoveredCategory ? 1 : 0.4)
                                : (activeCategories.length === 0 || activeCategories.includes(entry.name) ? 1 : 0.4);
                            const extraStyle = hoveredCategory && entry.name === hoveredCategory
                                ? { stroke: '#fff', strokeWidth: 2 }
                                : {};
                            return (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={entry.name === "OTHERS" ? '#CCCCCC' : categoryColorMap[entry.name]}
                                    opacity={opacity}
                                    style={entry.name === "OTHERS" ? { cursor: 'default' } : { cursor: 'pointer', ...extraStyle }}
                                />
                            );
                        })}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                </PieChart>
            </ResponsiveContainer>

            <div style={{ maxHeight: 300, overflowY: 'auto', width: "40%", paddingLeft: 15, paddingRight: 5 }}>
                <h4>{t('Dashboard.Details')}</h4>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                    {chartData.map((entry, index) => (
                        <li
                            key={`list-item-${index}`}
                            onMouseEnter={() => onListItemMouseEnter(entry.name)}
                            onMouseLeave={onListItemMouseLeave}
                            onClick={(e) => onListItemClick(entry.name, e)}
                            style={{
                                padding: '2px',
                                cursor: 'pointer',
                                backgroundColor: activeCategories.includes(entry.name) ? '#f0f0f0' : 'transparent',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'nowrap',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontSize: 13,
                            }}
                            title={`${entry.name}: ${entry.value} (${entry.percent.toFixed(2)}%)`}
                        >
                            <div>
                                <span
                                    style={{
                                        display: 'inline-block',
                                        width: '10px',
                                        height: '10px',
                                        backgroundColor: categoryColorMap[entry.name],
                                        borderRadius: '5px',
                                        marginRight: '8px',
                                    }}
                                ></span>
                                {entry.name}
                            </div>
                            : <strong style={{ paddingLeft: 5 }}>
                                {entry.value} ({entry.percent.toFixed(2)}%)
                            </strong>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
});

export default PieChartWithDetails;